import { Button } from '@/components/ui/button'
import { Search, Database, Share2, Sparkles } from 'lucide-react'

interface EmptyStateProps {
  title: string
  description?: string
  icon?: React.ReactNode | string
  action?: {
    label: string
    onClick: () => void
    variant?: 'primary' | 'secondary'
  }
  illustration?: 'search' | 'data' | 'connection' | 'emergence' | 'custom'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const illustrations = {
  search: (
    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-rsb-accent/20 to-rsb-accent/5 flex items-center justify-center">
      <Search className="w-7 h-7 text-rsb-accent/60" />
    </div>
  ),
  data: (
    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-rsb-accent/20 to-rsb-accent/5 flex items-center justify-center">
      <Database className="w-7 h-7 text-rsb-accent/60" />
    </div>
  ),
  connection: (
    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-domain-tech/20 to-domain-business/20 flex items-center justify-center">
      <Share2 className="w-7 h-7 text-rsb-accent/60" />
    </div>
  ),
  emergence: (
    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-domain-tech/20 to-domain-business/20 flex items-center justify-center">
      <Sparkles className="w-7 h-7 text-emerald-400/60" />
    </div>
  ),
  custom: null
}

export default function EmptyState({
  title,
  description,
  icon,
  action,
  illustration = 'data',
  size = 'md',
  className = ''
}: EmptyStateProps) {
  const sizeClasses = {
    sm: { container: 'py-8', title: 'text-lg', description: 'text-sm' },
    md: { container: 'py-12', title: 'text-xl', description: 'text-base' },
    lg: { container: 'py-16', title: 'text-2xl', description: 'text-lg' }
  }

  const config = sizeClasses[size]

  return (
    <div className={`flex flex-col items-center justify-center text-center max-w-md mx-auto ${config.container} ${className}`}>
      <div className="space-y-4">
        <div className="flex justify-center mb-4">
          {icon ? (
            typeof icon === 'string' ? (
              <div className="text-4xl opacity-60">{icon}</div>
            ) : icon
          ) : illustrations[illustration]}
        </div>

        <div className="space-y-2">
          <h3 className={`font-display font-bold text-white ${config.title}`}>{title}</h3>
          {description && (
            <p className={`text-muted-foreground leading-relaxed ${config.description}`}>{description}</p>
          )}
        </div>

        {action && (
          <div className="pt-2">
            <Button
              onClick={action.onClick}
              variant={action.variant === 'secondary' ? 'outline' : 'default'}
              className={action.variant === 'secondary'
                ? 'border-border text-white hover:bg-white/10'
                : 'bg-primary hover:bg-rsb-accent-glow text-white shadow-glow-sm'
              }
            >
              {action.label}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export const EmptySearchResults = ({ onClearSearch }: { onClearSearch?: () => void }) => (
  <EmptyState
    illustration="search"
    title="No results found"
    description="Try adjusting your filters or using different keywords."
    action={onClearSearch ? { label: "Clear filters", onClick: onClearSearch, variant: "secondary" } : undefined}
  />
)

export const EmptyDataState = ({ onRefresh }: { onRefresh?: () => void }) => (
  <EmptyState
    illustration="data"
    title="No data available"
    description="There is no information to display here yet."
    action={onRefresh ? { label: "Refresh", onClick: onRefresh, variant: "secondary" } : undefined}
  />
)

export const EmptyConnectionsState = ({ onNavigateConstellation }: { onNavigateConstellation?: () => void }) => (
  <EmptyState
    illustration="connection"
    title="No connections"
    description="This concept has no connections to other knowledge elements."
    action={onNavigateConstellation ? { label: "Explore Constellation", onClick: onNavigateConstellation, variant: "secondary" } : undefined}
  />
)

export const EmptyEmergencesState = ({ onIngest }: { onIngest?: () => void } = {}) => (
  <EmptyState
    illustration="emergence"
    title="No emergence detected"
    description="No new patterns found. Ingest more content to discover connections."
    icon="🌟"
    action={onIngest ? { label: "Ingest content", onClick: onIngest } : undefined}
  />
)

export const EmptyCanvasState = ({ onCreateBlock }: { onCreateBlock?: () => void } = {}) => (
  <EmptyState
    illustration="emergence"
    title="Seu canvas está vazio"
    description="Comece criando seu primeiro bloco de conhecimento. Pressione '/' para buscar ou crie um bloco manualmente."
    icon="✨"
    action={onCreateBlock ? { label: "Criar bloco", onClick: onCreateBlock } : undefined}
  />
)

export const EmptyConstellationState = ({ onIngest }: { onIngest?: () => void } = {}) => (
  <EmptyState
    illustration="connection"
    title="Seu conhecimento ainda não tem conexões"
    description="Ingira conteúdo para começar a construir sua rede de conhecimento. As conexões surgirão automaticamente."
    icon="🌌"
    action={onIngest ? { label: "Ingerir conteúdo", onClick: onIngest } : undefined}
  />
)

export const EmptyLibraryState = ({ onExplore }: { onExplore?: () => void } = {}) => (
  <EmptyState
    illustration="data"
    title="Nenhum conceito encontrado"
    description="Sua biblioteca está esperando por ideias. Ingira artigos, vídeos ou anotações para preencher este espaço."
    icon="📚"
    action={onExplore ? { label: "Explorar", onClick: onExplore } : undefined}
  />
)

export const EmptyIngestionsState = ({ onAdd }: { onAdd?: () => void } = {}) => (
  <EmptyState
    illustration="data"
    title="Nenhum conteúdo processado"
    description="Cole uma URL, texto ou faça upload de um arquivo para começar a construir sua base de conhecimento."
    icon="⚡"
    action={onAdd ? { label: "Adicionar conteúdo", onClick: onAdd } : undefined}
  />
)
